import React from 'react';
import { content } from '../content';
import { Project } from '../types';
import { ArrowRightIcon, CodeIcon, LiveDotIcon, WipIcon } from './Icon';
import '../styles/ProjectCard.css';

const { ui } = content;

/**
 * The two things a visitor can check without asking anyone. They sit on the
 * card rather than inside the case study, because the decision to open a card
 * is made while scanning the grid.
 */
const badgesFor = (project: Project) => {
  const kinds = new Set((project.details?.links ?? []).map((link) => link.kind));

  return [
    kinds.has('demo') && { id: 'demo', label: ui.badgeDemo, Icon: LiveDotIcon },
    kinds.has('repo') && { id: 'repo', label: ui.badgeRepo, Icon: CodeIcon },
    // Says why there is nothing to open before the visitor asks.
    project.details?.wip && { id: 'wip', label: ui.badgeWip, Icon: WipIcon },
  ].filter(Boolean) as { id: string; label: string; Icon: typeof CodeIcon }[];
};

type ProjectCardProps = {
  project: Project;
  onOpen: (project: Project) => void;
  /**
   * Position in the grid. The first card is above the fold on every screen
   * size and is the page's largest paint, so lazy-loading it delays the one
   * image the visitor is actually waiting for.
   */
  index: number;
};

/**
 * The card promises a click only when there is a case study behind it: the
 * title turns into a button that covers the whole card (one focus stop, full
 * card as the hit area), and the arrow is decoration next to it.
 */
const ProjectCard = ({ project, onOpen, index }: ProjectCardProps) => {
  const openable = Boolean(project.details);
  const badges = badgesFor(project);
  const eager = index === 0;

  return (
    <article
      className={`card${openable ? ' card--openable' : ''}`}
      style={{ ['--span' as string]: project.span }}
    >
      <div className="card__preview">
        {project.preview ? (
          <img
            src={project.preview}
            alt={ui.cardPreviewAlt(project.title)}
            loading={eager ? 'eager' : 'lazy'}
            fetchPriority={eager ? 'high' : 'auto'}
            decoding="async"
          />
        ) : (
          <div className="card__placeholder" aria-hidden>
            <span className="card__placeholder-mark">{project.number}</span>
          </div>
        )}
      </div>

      {badges.length > 0 && (
        <ul className="card__badges">
          {badges.map(({ id, label, Icon }) => (
            <li key={id} className={`badge badge--${id}`}>
              <Icon size={11} />
              {label}
            </li>
          ))}
        </ul>
      )}

      <div className="card__scrim" aria-hidden />

      <div className="card__body">
        <span className="card__number">{project.number}</span>
        <h3 className="card__title">{project.title}</h3>
        <p className="card__subtitle">{project.subtitle}</p>
        <ul className="card__stack">
          {project.stack.map((tech) => (
            <li key={tech} className="tag">
              {tech}
            </li>
          ))}
        </ul>
      </div>

      {openable && (
        <>
          <span className="card__open" aria-hidden>
            <ArrowRightIcon size={17} />
          </span>
          {/* Covers the whole card: one focus stop, the card as the hit area. */}
          <button
            type="button"
            className="card__link"
            aria-label={ui.cardOpen(project.title)}
            onClick={() => onOpen(project)}
          />
        </>
      )}
    </article>
  );
};

export default ProjectCard;
